import React from "react";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const includedItems = [{
  title: "Comprehensive Moisture & Thermal Control",
  description: "Sub-slab vapor barriers, rigid foam perimeter insulation, and closed-loop dehumidification suited to North Georgia's ground climate."
}, {
  title: "Code-Compliant Egress Windows & Safe Exits",
  description: "Excavated code-compliant egress window wells and exterior walkout access required by City of Brookhaven residential building codes."
}, {
  title: "Custom Finished Living Space Layouts",
  description: "Architectural framing, acoustical ceiling treatments, recessed lighting plans, luxury vinyl plank or hardwood flooring, and optional guest suites with full baths."
}, {
  title: "Dedicated Home Office & Media Room Conversions",
  description: "Dedicated acoustic insulation, in-wall low-voltage wiring, custom built-ins, and flexible power layouts for executive work suites and theater systems."
}];
export default function BasementRemodelingHero() {
  return <section id="basementremodelinghero" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Main Content Column */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-tight">
              Basement Finishing & Remodeling in Brookhaven
            </h1>

            {/* Direct 2-3 sentence answer */}
            <p className="mt-6 text-lg sm:text-xl text-foreground/90 leading-relaxed font-default">
              Basement finishing in Brookhaven transforms underutilized foundation levels into code-compliant, conditioned square footage that expands your family&apos;s living footprint. The complete process integrates structural moisture mitigation and drainage verification with full architectural framing, high-performance mechanical systems, and luxury finishes. From walkout basements near Murphey Candler Park to Historic Brookhaven terrace levels, proper execution adds enduring home value and tailored lifestyle utility.
            </p>

            {/* What's Included Card */}
            <Card className="mt-8 border border-border/80 bg-card shadow-sm rounded-xl">
              <CardContent className="p-6 sm:p-8">
                <h2 className="text-xl font-serif font-semibold text-foreground mb-6">
                  What&apos;s Included
                </h2>
                <ul className="space-y-5">
                  {includedItems.map((item, index) => <li key={index} data-index={index} className="flex items-start gap-3.5">
                      <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold text-foreground block text-base">
                          {item.title}
                        </span>
                        <span className="text-sm text-foreground/80 mt-0.5 block leading-normal">
                          {item.description}
                        </span>
                      </div>
                    </li>)}
                </ul>
              </CardContent>
            </Card>

            {/* Action Call */}
            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactRequestAnEstimatePage">
                  Request a Basement Estimate
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/OurProjectsPage">
                  Explore Brookhaven Projects
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual Showcase Column */}
          <div className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
            <div className="overflow-hidden rounded-xl border border-border shadow-md bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_finished_basement_living_room.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_finished_basement_living_room.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_luxury_finished_basement_living_room.png-wvc-sizes" alt="Architectural finished basement living room with natural light, custom built-ins, and luxury flooring in Brookhaven" className="w-full h-80 sm:h-96 object-cover object-center" loading="eager" />
            </div>

            <div className="overflow-hidden rounded-xl border border-border shadow-md bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_media_room_home_office.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_media_room_home_office.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_basement_media_room_home_office.png-wvc-sizes" alt="Modern basement media room and private office conversion with acoustic wood wall panels" className="w-full h-64 object-cover object-center" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}